import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import { articleContents } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articleContents.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-24 pb-16 min-h-screen bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-3xl text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-question-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-600 mb-6">您要查看的文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} | 征途国际官方下载`}
        description={article.excerpt}
        keywords={`征途国际,${article.categoryName},${article.title}`}
        canonicalUrl={`https://mobilestart.github.io/article/${article.slug}`}
      />

      <article className="pt-24 pb-16 md:pb-24 bg-background-50">
        <div className="mx-auto px-4 md:px-6 max-w-3xl">
          <div className="mb-6">
            <BreadcrumbNav
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors duration-200 whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="mb-8 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>

          <div
            className="article-content prose prose-gray max-w-none"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground-500 mb-1">作者</p>
                <p className="text-sm font-semibold text-foreground-950">{article.author}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">分享到：</span>
                <button className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回{article.categoryName}
            </Link>
          </div>
        </div>
      </article>

      <style>{`
        .article-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: oklch(var(--foreground-950));
          margin-top: 2rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid oklch(var(--primary-200));
        }
        .article-content h3 {
          font-size: 1.2rem;
          font-weight: 600;
          color: oklch(var(--foreground-900));
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .article-content p {
          font-size: 0.95rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 1rem;
        }
        .article-content strong {
          color: oklch(var(--foreground-950));
          font-weight: 600;
        }
        .article-content ul,
        .article-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .article-content li {
          font-size: 0.95rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 0.5rem;
        }
        .article-content ol li {
          list-style-type: decimal;
        }
        .article-content ul li {
          list-style-type: disc;
        }
      `}</style>
    </>
  );
}